"use client"

import * as React from 'react'
import { useParams, useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm, type CrudField, type CrudFormGroup } from '@open-mercato/ui/backend/CrudForm'
import { updateCrud, deleteCrud } from '@open-mercato/ui/backend/utils/crud'
import { apiCall, apiCallOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { LoadingMessage } from '@open-mercato/ui/backend/detail'
import { ErrorMessage } from '@open-mercato/ui/backend/detail'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'
import { Button } from '@open-mercato/ui/primitives/button'

type ShuntingDetail = {
  id: string
  stationId: string
  slotId: string
  status: string
  providerId: string | null
  requestType: string
  fromTrack: number | null
  toTrack: number | null
  trainPlanId: string | null
  wagons: Array<{ wagonId: string; evn: string; position?: number }> | null
  scheduledAt: string | null
  startedAt: string | null
  completedAt: string | null
  notes: string | null
}

type RouteParams = {
  id?: string | string[]
  slug?: string[]
}

function readRouteId(params?: RouteParams | null): string {
  if (!params) return ''
  if (typeof params.id === 'string') return params.id
  if (Array.isArray(params.id)) return params.id[0] ?? ''
  if (Array.isArray(params.slug)) return params.slug.at(-1) ?? ''
  return ''
}

const REQUEST_TYPE_KEYS: Record<string, string> = {
  arrival_shunting: 'shunting.type.arrival',
  departure_shunting: 'shunting.type.departure',
  repositioning: 'shunting.type.repositioning',
  coupling: 'shunting.type.coupling',
  decoupling: 'shunting.type.decoupling',
}

function mapApiToDetail(item: Record<string, unknown>): ShuntingDetail | null {
  const id = typeof item.id === 'string' ? item.id : null
  if (!id) return null
  return {
    id,
    stationId: typeof item.station_id === 'string' ? item.station_id : '',
    slotId: typeof item.slot_id === 'string' ? item.slot_id : '',
    status: typeof item.status === 'string' ? item.status : 'pending',
    providerId: typeof item.provider_id === 'string' ? item.provider_id : null,
    requestType: typeof item.request_type === 'string' ? item.request_type : '',
    fromTrack: typeof item.from_track === 'number' ? item.from_track : null,
    toTrack: typeof item.to_track === 'number' ? item.to_track : null,
    trainPlanId: typeof item.train_plan_id === 'string' ? item.train_plan_id : null,
    wagons: Array.isArray(item.wagons) ? item.wagons as ShuntingDetail['wagons'] : null,
    scheduledAt: typeof item.scheduled_at === 'string' ? item.scheduled_at : null,
    startedAt: typeof item.started_at === 'string' ? item.started_at : null,
    completedAt: typeof item.completed_at === 'string' ? item.completed_at : null,
    notes: typeof item.notes === 'string' ? item.notes : null,
  }
}

export default function ShuntingRequestDetailPage({ params }: { params?: RouteParams }) {
  const routeParams = useParams()
  const router = useRouter()
  const { confirm, ConfirmDialogElement } = useConfirmDialog()
  const t = useT()
  const scopeVersion = useOrganizationScopeVersion()
  const { runMutation } = useGuardedMutation<Record<string, unknown>>({
    contextId: 'shunting-request-detail',
  })
  const shuntingId = readRouteId(params) || readRouteId(routeParams as RouteParams | undefined)

  const [record, setRecord] = React.useState<ShuntingDetail | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  const getStatusLabel = React.useCallback((status: string) => {
    const fallbackMap: Record<string, string> = {
      pending: 'Pending',
      accepted: 'Accepted',
      in_progress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
    }
    return t(`shunting.status.${status}`, fallbackMap[status] ?? status)
  }, [t])

  const getRequestTypeLabel = React.useCallback((requestType: string) => {
    const key = REQUEST_TYPE_KEYS[requestType]
    const fallbackMap: Record<string, string> = {
      arrival_shunting: 'Arrival Shunting',
      departure_shunting: 'Departure Shunting',
      repositioning: 'Repositioning',
      coupling: 'Coupling',
      decoupling: 'Decoupling',
    }
    return key ? t(key, fallbackMap[requestType] ?? requestType) : requestType
  }, [t])

  const loadRecord = React.useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      type ResponseType = { items: Array<Record<string, unknown>>; total: number }
      const fallback: ResponseType = { items: [], total: 0 }
      const call = await apiCall<ResponseType>(
        `/api/shunting/shunting-requests?ids=${encodeURIComponent(shuntingId)}&pageSize=1`,
        undefined,
        { fallback },
      )
      if (!call.ok) {
        setError(t('shunting.error.load', 'Failed to load shunting request'))
        return
      }
      const items = call.result?.items ?? []
      const found = items.length > 0 ? items[0] : null
      if (found) {
        const mapped = mapApiToDetail(found)
        if (mapped) {
          setRecord(mapped)
        } else {
          setError(t('shunting.error.notFound', 'Shunting request not found'))
        }
      } else {
        setError(t('shunting.error.notFound', 'Shunting request not found'))
      }
    } catch {
      setError(t('shunting.error.load', 'Failed to load shunting request'))
    } finally {
      setIsLoading(false)
    }
  }, [shuntingId, t])

  React.useEffect(() => {
    if (shuntingId) {
      void loadRecord()
    } else {
      setIsLoading(false)
      setError(t('shunting.error.notFound', 'Shunting request not found'))
    }
  }, [shuntingId, loadRecord, scopeVersion, t])

  const handleTransition = React.useCallback(async (action: 'start' | 'complete') => {
    const labelMap = {
      start: t('shunting.confirm.start', 'Start this shunting maneuver?'),
      complete: t('shunting.confirm.complete', 'Mark this shunting maneuver as completed?'),
    }
    const confirmed = await confirm({ title: labelMap[action] })
    if (!confirmed) return

    try {
      await runMutation({
        operation: () => apiCallOrThrow(
          `/api/shunting/shunting-requests/${action}`,
          {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ id: shuntingId }),
          },
          { errorMessage: t('shunting.error.transition', 'Status transition failed') },
        ),
        context: {},
      })
      const flashMap = {
        start: t('shunting.flash.started', 'Shunting maneuver started'),
        complete: t('shunting.flash.completed', 'Shunting maneuver completed'),
      }
      flash(flashMap[action], 'success')
      await loadRecord()
    } catch (err) {
      const message = err instanceof Error ? err.message : t('shunting.error.transition', 'Status transition failed')
      flash(message, 'error')
    }
  }, [confirm, runMutation, shuntingId, t, loadRecord])

  const handleDelete = React.useCallback(async () => {
    const confirmed = await confirm({
      title: t('shunting.confirm.delete', 'Delete this shunting request?'),
      variant: 'destructive',
    })
    if (!confirmed) return

    try {
      await deleteCrud('shunting/shunting-requests', { id: shuntingId })
      flash(t('shunting.flash.deleted', 'Shunting request deleted'), 'success')
      router.push('/backend/shunting-requests')
    } catch (err) {
      const message = err instanceof Error ? err.message : t('shunting.error.delete', 'Failed to delete shunting request')
      flash(message, 'error')
    }
  }, [confirm, shuntingId, t, router])

  const isEditable = record ? record.status !== 'completed' && record.status !== 'cancelled' : false
  const isReadonly = !isEditable

  const fields = React.useMemo<CrudField[]>(() => [
    {
      id: 'requestType',
      label: t('shunting.fields.requestType', 'Request Type'),
      type: 'select',
      required: true,
      readOnly: isReadonly,
      options: [
        { value: 'arrival_shunting', label: t('shunting.type.arrival', 'Arrival Shunting') },
        { value: 'departure_shunting', label: t('shunting.type.departure', 'Departure Shunting') },
        { value: 'repositioning', label: t('shunting.type.repositioning', 'Repositioning') },
        { value: 'coupling', label: t('shunting.type.coupling', 'Coupling') },
        { value: 'decoupling', label: t('shunting.type.decoupling', 'Decoupling') },
      ],
    },
    {
      id: 'status',
      label: t('shunting.fields.status', 'Status'),
      type: 'text',
      readOnly: true,
    },
    {
      id: 'providerId',
      label: t('shunting.fields.providerId', 'Shunting Provider'),
      type: 'text',
      readOnly: isReadonly,
      placeholder: 'Provider UUID',
    },
    {
      id: 'fromTrack',
      label: t('shunting.fields.fromTrack', 'From Track'),
      type: 'number',
      readOnly: isReadonly,
    },
    {
      id: 'toTrack',
      label: t('shunting.fields.toTrack', 'To Track'),
      type: 'number',
      readOnly: isReadonly,
    },
    {
      id: 'trainPlanId',
      label: t('shunting.fields.trainPlanId', 'Train Plan'),
      type: 'text',
      readOnly: isReadonly,
      placeholder: 'Train Plan UUID',
    },
    {
      id: 'scheduledAt',
      label: t('shunting.fields.scheduledAt', 'Scheduled At'),
      type: 'datetime-local',
      readOnly: isReadonly,
    },
    {
      id: 'notes',
      label: t('shunting.fields.notes', 'Notes'),
      type: 'textarea',
      readOnly: isReadonly,
    },
  ], [t, isReadonly])

  const groups = React.useMemo<CrudFormGroup[]>(() => [
    {
      id: 'maneuver',
      title: t('shunting.groups.maneuver', 'Maneuver Details'),
      fields: ['requestType', 'status', 'fromTrack', 'toTrack'],
    },
    {
      id: 'assignment',
      title: t('shunting.groups.assignment', 'Assignment'),
      fields: ['providerId', 'trainPlanId', 'scheduledAt'],
    },
    {
      id: 'additional',
      title: t('shunting.groups.additional', 'Additional'),
      fields: ['notes'],
    },
  ], [t])

  if (isLoading) return <Page><PageBody><LoadingMessage label={t('shunting.loading', 'Loading...')} /></PageBody></Page>
  if (error || !record) return <Page><PageBody><ErrorMessage label={error ?? t('shunting.error.notFound', 'Shunting request not found')} /></PageBody></Page>

  const canStart = record.status === 'accepted'
  const canComplete = record.status === 'in_progress'

  const statusLabel = getStatusLabel(record.status)

  return (
    <Page>
      <PageBody>
        <div className="mb-4 flex items-center gap-2">
          {canStart && (
            <Button type="button" variant="default" onClick={() => void handleTransition('start')}>
              {t('shunting.actions.start', 'Start Maneuver')}
            </Button>
          )}
          {canComplete && (
            <Button type="button" variant="default" onClick={() => void handleTransition('complete')}>
              {t('shunting.actions.complete', 'Complete Maneuver')}
            </Button>
          )}
          {isEditable && (
            <Button type="button" variant="destructive" onClick={() => void handleDelete()}>
              {t('shunting.actions.delete', 'Delete')}
            </Button>
          )}
        </div>

        <CrudForm
          title={`${t('shunting.detail.title', 'Shunting Request')} — ${statusLabel}`}
          backHref="/backend/shunting-requests"
          fields={fields}
          groups={groups}
          initialValues={{
            requestType: record.requestType,
            status: statusLabel,
            providerId: record.providerId ?? '',
            fromTrack: record.fromTrack ?? '',
            toTrack: record.toTrack ?? '',
            trainPlanId: record.trainPlanId ?? '',
            scheduledAt: record.scheduledAt ? record.scheduledAt.slice(0, 16) : '',
            notes: record.notes ?? '',
          }}
          submitLabel={isEditable
            ? t('shunting.actions.update', 'Save Changes')
            : undefined}
          cancelHref="/backend/shunting-requests"
          onSubmit={isEditable
            ? async (values) => {
              const payload: Record<string, unknown> = { id: shuntingId }
              if (values.requestType !== undefined) payload.requestType = values.requestType
              if (values.providerId !== undefined && values.providerId !== '') payload.providerId = values.providerId
              if (values.fromTrack !== undefined && values.fromTrack !== '') payload.fromTrack = Number(values.fromTrack)
              if (values.toTrack !== undefined && values.toTrack !== '') payload.toTrack = Number(values.toTrack)
              if (values.trainPlanId !== undefined && values.trainPlanId !== '') payload.trainPlanId = values.trainPlanId
              if (values.scheduledAt !== undefined && values.scheduledAt !== '') payload.scheduledAt = values.scheduledAt
              if (values.notes !== undefined) payload.notes = values.notes

              await updateCrud('shunting/shunting-requests', payload)
              flash(t('shunting.flash.updated', 'Shunting request updated'), 'success')
              await loadRecord()
            }
            : undefined}
        />
      </PageBody>
      {ConfirmDialogElement}
    </Page>
  )
}
